import { useState } from "react";
import { Loader2, Pencil, Plus, Trash2, Users } from "lucide-react";
import { useAuth } from "@/components/providers/auth-provider";
import {
  useTeam,
  useTeams,
  useDeleteTeam,
  useRemoveTeamMember,
  type TeamSummary,
} from "@/hooks/use-teams";
import { TeamEditorDialog } from "@/components/configuracion/team-editor-dialog";
import { TeamMembersDialog } from "@/components/configuracion/team-members-dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

// Mejora D (changelog cliente): página admin para gestionar equipos.
// Solo accesible para usuarios con rol 'admin' en Keycloak. El resto
// llega aquí y ve un aviso de "requiere permisos de admin".

export default function ConfigEquiposPage() {
  const { isAdmin, loading } = useAuth();
  const { data: teams, isLoading, isError } = useTeams();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<TeamSummary | null>(null);
  const [pendingDelete, setPendingDelete] = useState<TeamSummary | null>(null);
  const [membersOf, setMembersOf] = useState<TeamSummary | null>(null);

  const deleteMutation = useDeleteTeam();

  if (loading) {
    return (
      <div className="p-6">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-6 max-w-3xl">
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
          La gestión de equipos requiere permisos de administrador. Si
          necesitas crear o modificar equipos, contacta con tu
          administrador de LicitaAI.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-6 h-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">Equipos</h1>
          </div>
          <p className="text-sm text-gray-500 max-w-3xl">
            Gestiona los equipos de trabajo (centros y grupos
            transversales). Cada licitación pertenece a un equipo y solo
            los miembros del equipo (y los administradores) la ven.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setEditorOpen(true);
          }}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Crear equipo
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando equipos…
        </div>
      )}

      {isError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          Error cargando los equipos. Recarga la página.
        </div>
      )}

      {teams && teams.length === 0 && (
        <div className="p-6 border border-dashed border-gray-300 rounded-md text-sm text-gray-500 text-center">
          Aún no hay equipos. Crea el primero para empezar a separar
          licitaciones por centro o equipo.
        </div>
      )}

      {teams && teams.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-700">
                  Nombre
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">
                  Descripción
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">
                  Miembros
                </th>
                <th className="px-4 py-3 text-right font-medium text-gray-700 w-32">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {teams.map((team) => (
                <tr key={team.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold text-gray-900">
                    {team.name}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {team.description ?? (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setMembersOf(team)}
                      className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                    >
                      <Users className="w-3 h-3" />
                      {team.member_count}{" "}
                      {team.member_count === 1 ? "miembro" : "miembros"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(team);
                          setEditorOpen(true);
                        }}
                        className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                        aria-label="Editar equipo"
                        title="Editar"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingDelete(team)}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                        aria-label="Eliminar equipo"
                        title="Eliminar"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TeamEditorDialog
        isOpen={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditing(null);
        }}
        team={editing}
      />

      {membersOf && (
        <MembersDialogConnected
          team={membersOf}
          onClose={() => setMembersOf(null)}
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="Eliminar equipo"
        description={
          pendingDelete
            ? `¿Eliminar "${pendingDelete.name}"? Las licitaciones asociadas dejarán de tener equipo y necesitarás reasignarlas.`
            : ""
        }
        variant="destructive"
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          deleteMutation.mutate(pendingDelete.id, {
            onSuccess: () => setPendingDelete(null),
          });
        }}
      />
    </div>
  );
}

/**
 * Carga el detalle del team antes de pasarlo al diálogo de miembros (que
 * necesita la lista completa de members con email/nombre).
 */
function MembersDialogConnected({
  team,
  onClose,
}: {
  team: TeamSummary;
  onClose: () => void;
}) {
  const { data, isLoading } = useTeam(team.id);
  const removeMutation = useRemoveTeamMember();

  return (
    <TeamMembersDialog
      isOpen={true}
      onClose={onClose}
      team={data ?? null}
      isLoading={isLoading}
      onRemoveMember={(userId) =>
        removeMutation.mutate({ teamId: team.id, userId })
      }
      removingUserId={
        removeMutation.isPending ? removeMutation.variables?.userId : undefined
      }
    />
  );
}
